import React, { useEffect, useState } from 'react';
import { 
  X, 
  ShieldCheck, 
  Crown, 
  Zap, 
  ClipboardList, 
  Calendar, 
  Users, 
  Receipt as ReceiptIcon, 
  Package, 
  BarChart3, 
  PenTool, 
  Settings, 
  HelpCircle, 
  LogOut, 
  Boxes,
  Stethoscope,
  Syringe,
  FileArchive,
  Building2,
  Share2,
  ChevronDown,
  MessageCircle
} from 'lucide-react';
import { UserPlanTier } from './PlanUpgradeModal';
import { ActiveView } from '../hooks/useErpPlaygroundState';
import FontSizeSelector from '../../../components/FontSizeSelector';
import { useTeamPermissions } from '../../../contexts/TeamPermissionsContext';

interface ErpSidebarDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  displayName: string;
  logoUrl?: string;
  tagline?: string;
  nicheBadge?: string;
  planTier: UserPlanTier;
  onNavigate: (view: ActiveView) => void;
  onOpenHelp: () => void;
  onOpenFiscalConfig?: () => void;
  onLogout?: () => void;
  canAccess: (requiredTier: 'free' | 'pro' | 'premium' | 'enterprise') => boolean;
  handleActionClick: (
    requiredTier: 'free' | 'pro' | 'premium' | 'enterprise',
    action: () => void,
    featureName: string,
    featureDesc?: string
  ) => void;
}

export default function ErpSidebarDrawer({
  isOpen,
  onClose,
  displayName,
  logoUrl,
  tagline,
  nicheBadge,
  planTier,
  onNavigate,
  onOpenHelp,
  onOpenFiscalConfig,
  onLogout,
  canAccess,
  handleActionClick
}: ErpSidebarDrawerProps) {
  const { isMaster, can } = useTeamPermissions();

  // Sanfona dos Módulos Empresa: aberta por padrão no plano Empresa, recolhida para Especialista, Pro e Grátis
  const [isEnterpriseExpanded, setIsEnterpriseExpanded] = useState<boolean>(
    planTier === 'enterprise'
  );

  // Fechar ao pressionar ESC
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Prevenir scroll do body quando aberto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const getInitials = (name: string) => {
    if (!name) return 'RM';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const handleNav = (
    view: ActiveView,
    requiredTier: 'free' | 'pro' | 'premium' | 'enterprise' = 'free',
    title: string = '',
    desc: string = ''
  ) => {
    onClose();
    if (requiredTier === 'free' || canAccess(requiredTier)) {
      onNavigate(view);
    } else {
      handleActionClick(requiredTier, () => onNavigate(view), title, desc);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 transition-all duration-300 ${
        isOpen ? 'opacity-100 pointer-events-auto visible' : 'opacity-0 pointer-events-none invisible'
      }`}
      aria-hidden={!isOpen}
    >
      {/* Backdrop escurecido com fade suave */}
      <div
        onClick={onClose}
        className="fixed inset-0 bg-neutral-950/60 backdrop-blur-xs transition-opacity duration-300"
      />

      {/* Drawer M3 com raio de 28dp (rounded-r-[28px]) */}
      <div
        className={`fixed top-0 bottom-0 left-0 w-[85%] max-w-85 bg-white text-zinc-900 shadow-2xl flex flex-col z-50 transform transition-transform duration-300 ease-out rounded-r-[28px] overflow-hidden ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Cabeçalho de Perfil M3 (Tema Claro Harmonizado) */}
        <div className="p-4 bg-white text-zinc-900 border-b border-zinc-200/80 flex flex-col gap-3 relative shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3.5 right-3.5 w-9 h-9 rounded-full flex items-center justify-center text-zinc-400 hover:text-zinc-800 hover:bg-zinc-100 active:scale-95 transition-all cursor-pointer"
            title="Fechar menu"
            aria-label="Fechar menu lateral"
          >
            <X className="h-4.5 w-4.5" />
          </button>

          <div className="flex items-center gap-3 pr-8">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={displayName || 'Logotipo'}
                className="w-11 h-11 rounded-2xl object-cover border border-zinc-200 bg-white shrink-0 shadow-2xs"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling;
                  if (fallback) fallback.classList.remove('hidden');
                }}
              />
            ) : null}
            <div
              className={`w-11 h-11 rounded-2xl bg-amber-500/10 text-amber-700 border border-amber-500/20 flex items-center justify-center font-black text-sm shrink-0 shadow-2xs ${
                logoUrl ? 'hidden' : ''
              }`}
            >
              {getInitials(displayName)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <h3 className="font-bold text-sm text-zinc-900 truncate">
                  {displayName || 'Meu Negócio'}
                </h3>
                <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />
              </div>
              <p className="text-[11px] text-zinc-500 truncate">
                {tagline || nicheBadge || 'Profissional R-MAX'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2 border-t border-zinc-100">
            <span className={`text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full flex items-center gap-1 ${
              planTier === 'enterprise'
                ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                : planTier === 'premium' 
                ? 'bg-amber-50 text-amber-800 border border-amber-300/80' 
                : planTier === 'pro' 
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                : 'bg-zinc-100 text-zinc-600 border border-zinc-200'
            }`}>
              {planTier === 'enterprise' && <ShieldCheck className="h-3 w-3" />}
              {planTier === 'premium' && <Crown className="h-3 w-3" />}
              {planTier === 'pro' && <Zap className="h-3 w-3" />}
              {planTier === 'enterprise' ? 'Plano Empresa' : planTier === 'premium' ? 'Plano Especialista' : planTier === 'pro' ? 'Plano PRO' : 'Plano Gratuito'}
            </span>
            {nicheBadge && (
              <span className="text-[10px] font-medium text-zinc-600 truncate bg-zinc-100 border border-zinc-200/80 px-2 py-0.5 rounded-md">
                {nicheBadge}
              </span>
            )}
          </div>
        </div>

        {/* Lista de Navegação M3 (Sem chevrons e com touch target 48dp) */}
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-4">
          {/* Seção 1: Navegação Operacional Principal */}
          <div>
            <div className="px-3 mb-1.5 text-[10px] font-black text-zinc-400 uppercase tracking-wider">
              Navegação Rápida
            </div>
            <div className="space-y-1">
              <button
                type="button"
                onClick={() => handleNav('orcamento', 'free', 'Orçamentos Rápidos')}
                className="w-full flex items-center justify-between px-3.5 py-2.5 min-h-11.5 rounded-2xl text-xs font-semibold text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100 active:bg-zinc-200/70 active:scale-[0.99] transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <ClipboardList className="h-4.5 w-4.5 text-zinc-500 group-hover:text-amber-600 transition-colors shrink-0" />
                  <span className="truncate">Orçamentos Rápidos</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleNav('agenda', 'premium', 'Agenda & Atendimentos', 'Grade do dia, link online, equipe e horários fixos — tudo em um só lugar.')}
                className="w-full flex items-center justify-between px-3.5 py-2.5 min-h-11.5 rounded-2xl text-xs font-semibold text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100 active:bg-zinc-200/70 active:scale-[0.99] transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Calendar className="h-4.5 w-4.5 text-zinc-500 group-hover:text-teal-600 transition-colors shrink-0" />
                  <span className="truncate">Agenda & Atendimentos</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleNav('clientes', 'pro', 'Meus Clientes', 'Histórico completo e contatos dos seus clientes.')}
                className="w-full flex items-center justify-between px-3.5 py-2.5 min-h-11.5 rounded-2xl text-xs font-semibold text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100 active:bg-zinc-200/70 active:scale-[0.99] transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Users className="h-4.5 w-4.5 text-zinc-500 group-hover:text-blue-600 transition-colors shrink-0" />
                  <span className="truncate">Meus Clientes</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleNav('recibo', 'premium', 'Recibos PIX', 'Gere recibos com comprovante e QR Code.')}
                className="w-full flex items-center justify-between px-3.5 py-2.5 min-h-11.5 rounded-2xl text-xs font-semibold text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100 active:bg-zinc-200/70 active:scale-[0.99] transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <ReceiptIcon className="h-4.5 w-4.5 text-zinc-500 group-hover:text-emerald-600 transition-colors shrink-0" />
                  <span className="truncate">Recibos PIX Profissionais</span>
                </div>
              </button>

              {/* Minha Equipe (Colaboradores e Permissões) */}
              {isMaster && (
                <button
                  type="button"
                  onClick={() => handleNav('equipe' as any, 'premium', 'Minha Equipe (Até 10 Membros)', 'Adicione até 10 colaboradores da sua empresa com travas e permissões personalizadas.')}
                  className="w-full flex items-center justify-between px-3.5 py-2.5 min-h-11.5 rounded-2xl text-xs font-semibold text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100 active:bg-zinc-200/70 active:scale-[0.99] transition-all text-left group cursor-pointer"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Users className="h-4.5 w-4.5 text-zinc-500 group-hover:text-emerald-600 transition-colors shrink-0" />
                    <span className="truncate">Minha Equipe</span>
                  </div>
                </button>
              )}

              {can('view_cost_center') && (
                <button
                  type="button"
                  onClick={() => handleNav('custos', 'pro', 'Centro de Custos', 'Gerencie insumos, matérias-primas, receitas e catálogo de produtos.')}
                  className="w-full flex items-center justify-between px-3.5 py-2.5 min-h-11.5 rounded-2xl text-xs font-semibold text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100 active:bg-zinc-200/70 active:scale-[0.99] transition-all text-left group cursor-pointer"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Package className="h-4.5 w-4.5 text-zinc-500 group-hover:text-purple-600 transition-colors shrink-0" />
                    <span className="truncate">Custos, Produtos & Insumos</span>
                  </div>
                </button>
              )}

              {can('view_executive_cockpit') && (
                <button
                  type="button"
                  onClick={() => handleNav('cockpit', 'pro', 'Painel Financeiro', 'Resumo financeiro e visão de lucro real.')}
                  className="w-full flex items-center justify-between px-3.5 py-2.5 min-h-11.5 rounded-2xl text-xs font-semibold text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100 active:bg-zinc-200/70 active:scale-[0.99] transition-all text-left group cursor-pointer"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <BarChart3 className="h-4.5 w-4.5 text-zinc-500 group-hover:text-indigo-600 transition-colors shrink-0" />
                    <span className="truncate">Painel Financeiro & Lucro</span>
                  </div>
                </button>
              )}
            </div>
          </div>

          <div className="h-px bg-zinc-100 mx-2" />

          {/* Seção 2: Módulos Plano Empresa (Sanfona Retrátil M3) */}
          <div className="rounded-2xl border border-indigo-100/80 bg-indigo-50/20 overflow-hidden transition-all">
            <button
              type="button"
              onClick={() => setIsEnterpriseExpanded(prev => !prev)}
              aria-expanded={isEnterpriseExpanded}
              aria-controls="drawer-enterprise-modules"
              className="w-full p-2.5 px-3 flex items-center justify-between hover:bg-indigo-50/60 active:bg-indigo-100/50 transition-colors cursor-pointer select-none text-left"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[10px] font-black text-indigo-700 uppercase tracking-wider truncate">
                  Módulos Plano Empresa
                </span>
                <span className="text-[9px] bg-indigo-100 text-indigo-800 px-1.5 py-0.2 rounded-full font-bold shrink-0">
                  R$ 99
                </span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0 ml-2">
                <span className="text-[9px] font-bold text-indigo-600">
                  {isEnterpriseExpanded ? 'Recolher' : '6 módulos'}
                </span>
                <ChevronDown
                  className={`h-3.5 w-3.5 text-indigo-600 transition-transform duration-200 ${
                    isEnterpriseExpanded ? 'rotate-180' : ''
                  }`}
                />
              </div>
            </button>

            <div
              id="drawer-enterprise-modules"
              className={`space-y-1 transition-all duration-300 ease-in-out overflow-hidden ${
                isEnterpriseExpanded ? 'max-h-[500px] opacity-100 p-1 pt-0' : 'max-h-0 opacity-0 p-0 pointer-events-none'
              }`}
            >
              <button
                type="button"
                onClick={() => handleNav('estoque_hub', 'enterprise', 'Estoque & Lotes', 'Balancete financeiro, lotes por validade e extrato de movimentos.')}
                className="w-full flex items-center justify-between px-3.5 py-2.5 min-h-11.5 rounded-2xl text-xs font-semibold text-zinc-700 hover:text-indigo-800 hover:bg-indigo-50/60 active:bg-indigo-100/60 active:scale-[0.99] transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Boxes className="h-4.5 w-4.5 text-zinc-500 group-hover:text-indigo-600 transition-colors shrink-0" />
                  <span className="truncate">Estoque & Lotes</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleNav('prontuario_soap', 'enterprise', 'Prontuário SOAP & CRM', 'Registro clínico com sinais vitais e checkout integrado com baixa de insumos.')}
                className="w-full flex items-center justify-between px-3.5 py-2.5 min-h-11.5 rounded-2xl text-xs font-semibold text-zinc-700 hover:text-blue-800 hover:bg-blue-50/60 active:bg-blue-100/60 active:scale-[0.99] transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Stethoscope className="h-4.5 w-4.5 text-zinc-500 group-hover:text-blue-600 transition-colors shrink-0" />
                  <span className="truncate">Prontuário SOAP</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleNav('retorno_vacinas', 'enterprise', 'Central de Retorno de Vacinas', 'Lembretes de retornos no WhatsApp com link da agenda.')}
                className="w-full flex items-center justify-between px-3.5 py-2.5 min-h-11.5 rounded-2xl text-xs font-semibold text-zinc-700 hover:text-emerald-800 hover:bg-emerald-50/60 active:bg-emerald-100/60 active:scale-[0.99] transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Syringe className="h-4.5 w-4.5 text-zinc-500 group-hover:text-emerald-600 transition-colors shrink-0" />
                  <span className="truncate">Vacinas & Retornos</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleNav('cofre_exames', 'enterprise', 'Cofre de Exames & DICOM', 'Armazenamento seguro R2 com guarda legal de 5 anos e negatoscópio W/L.')}
                className="w-full flex items-center justify-between px-3.5 py-2.5 min-h-11.5 rounded-2xl text-xs font-semibold text-zinc-700 hover:text-purple-800 hover:bg-purple-50/60 active:bg-purple-100/60 active:scale-[0.99] transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FileArchive className="h-4.5 w-4.5 text-zinc-500 group-hover:text-purple-600 transition-colors shrink-0" />
                  <span className="truncate">Cofre de Exames</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleNav('portal_contador', 'enterprise', 'Portal do Contador', 'Fechamento fiscal, livro caixa analítico e exportação para Domínio e Alterdata.')}
                className="w-full flex items-center justify-between px-3.5 py-2.5 min-h-11.5 rounded-2xl text-xs font-semibold text-zinc-700 hover:text-blue-800 hover:bg-blue-50/60 active:bg-blue-100/60 active:scale-[0.99] transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Building2 className="h-4.5 w-4.5 text-zinc-500 group-hover:text-blue-600 transition-colors shrink-0" />
                  <span className="truncate">Portal do Contador</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => {
                  onClose();
                  if (onOpenFiscalConfig) {
                    onOpenFiscalConfig();
                  }
                }}
                className="w-full flex items-center justify-between px-3.5 py-2.5 min-h-11.5 rounded-2xl text-xs font-semibold text-zinc-700 hover:text-indigo-800 hover:bg-indigo-50/60 active:bg-indigo-100/60 active:scale-[0.99] transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <ShieldCheck className="h-4.5 w-4.5 text-zinc-500 group-hover:text-indigo-600 transition-colors shrink-0" />
                  <span className="truncate">Emissão Fiscal NFC-e (SEFAZ)</span>
                </div>
                <span className="text-[9px] bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded font-bold uppercase shrink-0">
                  NFC-e
                </span>
              </button>
            </div>
          </div>

          <div className="h-px bg-zinc-100 mx-2" />

          {/* Seção 3: Preferências & Acessibilidade */}
          <div>
            <div className="px-3 mb-1.5 text-[10px] font-black text-zinc-400 uppercase tracking-wider">
              Preferências & Ajustes
            </div>
            
            {/* Seletor M3 de Tamanho de Fonte */}
            <div className="p-3 bg-zinc-50/90 rounded-2xl border border-zinc-100/90 mx-1 mb-2">
              <div className="text-[10px] font-black text-zinc-500 uppercase tracking-wider mb-2">
                Tamanho do Texto
              </div>
              <FontSizeSelector variant="compact" theme="light" className="w-full" />
            </div>

            <div className="space-y-1">
              <button
                type="button"
                onClick={() => handleNav('cadastro', 'free')}
                className="w-full flex items-center justify-between px-3.5 py-2.5 min-h-11.5 rounded-2xl text-xs font-semibold text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100 active:bg-zinc-200/70 active:scale-[0.99] transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Settings className="h-4.5 w-4.5 text-zinc-500 group-hover:text-zinc-800 transition-colors shrink-0" />
                  <span className="truncate">Configurações do Perfil</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleNav('assinatura', 'premium', 'Assinatura Digital', 'Colha assinaturas na tela do celular.')}
                className="w-full flex items-center justify-between px-3.5 py-2.5 min-h-11.5 rounded-2xl text-xs font-semibold text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100 active:bg-zinc-200/70 active:scale-[0.99] transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <PenTool className="h-4.5 w-4.5 text-zinc-500 group-hover:text-zinc-800 transition-colors shrink-0" />
                  <span className="truncate">Assinatura Digital de Clientes</span>
                </div>
              </button>
            </div>
          </div>

          <div className="h-px bg-zinc-100 mx-2" />

          {/* Seção 4: Suporte & Comunidade */}
          <div>
            <div className="px-3 mb-1.5 text-[10px] font-black text-zinc-400 uppercase tracking-wider">
              Ajuda & Benefícios
            </div>
            <div className="space-y-1">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenHelp();
                }}
                className="w-full flex items-center justify-between px-3.5 py-2.5 min-h-11.5 rounded-2xl text-xs font-semibold text-zinc-700 hover:text-amber-800 hover:bg-amber-50/60 active:bg-amber-100/60 active:scale-[0.99] transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <HelpCircle className="h-4.5 w-4.5 text-zinc-500 group-hover:text-amber-600 transition-colors shrink-0" />
                  <span className="truncate">Central de Ajuda & Tira-Dúvidas</span>
                </div>
              </button>

              <a
                href={`https://wa.me/5511920897453?text=${encodeURIComponent(
                  'Olá! Estou testando o R-MAX e gostaria de relatar um feedback / sugestão:'
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
                className="w-full flex items-center justify-between px-3.5 py-2.5 min-h-11.5 rounded-2xl text-xs font-semibold text-emerald-800 hover:text-emerald-950 hover:bg-emerald-50 active:bg-emerald-100/70 active:scale-[0.99] transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <MessageCircle className="h-4.5 w-4.5 text-emerald-600 group-hover:text-emerald-700 transition-colors shrink-0" />
                  <span className="truncate">Relatar Erro ou Sugestão</span>
                </div>
              </a>

              <a
                href="/influenciador"
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
                className="w-full flex items-center justify-between px-3.5 py-2.5 min-h-11.5 rounded-2xl text-xs font-semibold text-zinc-700 hover:text-zinc-950 hover:bg-zinc-100 active:bg-zinc-200/70 active:scale-[0.99] transition-all text-left group cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Share2 className="h-4.5 w-4.5 text-zinc-500 group-hover:text-zinc-800 transition-colors shrink-0" />
                  <span className="truncate">Programa de Influenciadores</span>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Rodapé M3 com Safe-Area e Ação de Sair */}
        <div className="p-3.5 pb-6 border-t border-zinc-100 bg-zinc-50/90 backdrop-blur-xs flex items-center justify-between text-xs shrink-0">
          <div className="flex flex-col">
            <span className="text-[11px] font-bold text-zinc-600">
              R-MAX Especialista
            </span>
            <span className="text-[9px] text-zinc-400 font-medium">
              Precificação Inteligente
            </span>
          </div>
          {onLogout && (
            <button
              type="button"
              onClick={() => {
                onClose();
                onLogout();
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-rose-600 hover:bg-rose-50 active:scale-95 font-bold text-xs transition-all cursor-pointer"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span>Sair</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
